// Audio Store - Audio playback state management
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AudioClip } from '../api/types';
import { audioEngine, defaultEffects, type AudioEffects } from '../audio/AudioEngine';
import clipsApi from '../api/clips';

export const useAudioStore = defineStore('audio', () => {
  // State
  const currentClip = ref<AudioClip | null>(null);
  const isPlaying = ref(false);
  const progress = ref(0); // 0 to 1
  const effects = ref<AudioEffects>({ ...defaultEffects });

  // Getters
  const currentTitle = computed(() => currentClip.value?.title ?? '');
  const currentActor = computed(() => currentClip.value?.actor.name ?? '');
  const currentDuration = computed(() => currentClip.value?.duration_ms ?? 0);
  const formattedDuration = computed(() => formatDuration(currentDuration.value));
  const formattedProgress = computed(() => {
    const elapsed = currentDuration.value * progress.value;
    return formatDuration(elapsed);
  });

  // Helper function
  function formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  // Setup audio engine callbacks
  audioEngine.onPlaybackEnd(() => {
    isPlaying.value = false;
    progress.value = 0;
  });

  audioEngine.onProgress((p) => {
    progress.value = p;
  });

  // Actions
  async function playClip(clip: AudioClip): Promise<void> {
    const streamUrl = clipsApi.getStreamUrl(clip.id);
    
    currentClip.value = clip;
    isPlaying.value = true;
    progress.value = 0;

    try {
      await audioEngine.play(clip, streamUrl, effects.value);
    } catch (e) {
      console.error('Failed to play clip:', e);
      isPlaying.value = false;
    }
  }

  function pause(): void {
    audioEngine.pause();
    isPlaying.value = false;
  }

  function resume(): void {
    audioEngine.resume();
    isPlaying.value = true;
  }

  function togglePlayPause(): void {
    if (isPlaying.value) {
      pause();
    } else if (currentClip.value) {
      resume();
    }
  }

  function stop(): void {
    audioEngine.stop();
    isPlaying.value = false;
    progress.value = 0;
  }

  function panicStop(): void {
    audioEngine.panicStop();
    isPlaying.value = false;
    progress.value = 0;
    currentClip.value = null;
  }

  function setPitch(semitones: number): void {
    effects.value.pitch = semitones;
    audioEngine.setPitch(semitones);
  }

  function setReverb(wet: number): void {
    effects.value.reverb = wet;
    audioEngine.setReverb(wet);
  }

  function setSpeed(rate: number): void {
    effects.value.speed = rate;
    audioEngine.setSpeed(rate);
  }

  function setVolume(db: number): void {
    effects.value.volume = db;
    audioEngine.setVolume(db);
  }

  function resetEffects(): void {
    effects.value = { ...defaultEffects };
    audioEngine.setPitch(defaultEffects.pitch);
    audioEngine.setReverb(defaultEffects.reverb);
    audioEngine.setSpeed(defaultEffects.speed);
    audioEngine.setVolume(defaultEffects.volume);
  }

  return {
    // State
    currentClip,
    isPlaying,
    progress,
    effects,
    // Getters
    currentTitle,
    currentActor,
    currentDuration,
    formattedDuration,
    formattedProgress,
    // Actions
    playClip,
    pause,
    resume,
    togglePlayPause,
    stop,
    panicStop,
    setPitch,
    setReverb,
    setSpeed,
    setVolume,
    resetEffects,
  };
});

export default useAudioStore;
